import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { StateType } from '../state/state'
import {
  MARGINS,
  DRAGGING_HANDLE_AXIS_RADIUS,
  HANDLE_AXIS_RADIUS,
  ARROW_MARKER_ID,
  CUTOFF_ID,
  LABEL_SHADOW_ID,
  CIRCLE_SHADOW_ID,
} from '../constants'
import { FilterableAxis } from './FilterableAxis'
import { Grid } from './Grid'
import { Paths } from './Paths'
import { Dots } from './Dots'
import { computeScales } from '../utils'
import { isUndefined } from 'lodash-es'
import { ShadowFilter } from '../../../components/ui/ShadowFilter'
import { Labels } from './Labels'

interface Props {
  className?: string
  state?: StateType
  parentWidth?: number
  parentHeight?: number
  width: number
  height: number
  x?: number
  y?: number
}

export const Scatterplot: React.FC<Props> = inject('state')(
  observer(({ className = '', state, width, height, x = 0, y = 0 }) => {
    const {
      scaleType,
      xDomain,
      yDomain,
      xDomainGreater,
      yDomainGreater,
      setIsXAxisDragging,
      setIsYAxisDragging,
      xAxisName,
      yAxisName,
      debug,
    } = state.ScatterplotState

    ///////////////////////////////////////////////////////////////////////////

    const scatterWidth = width - MARGINS.left - MARGINS.right
    const scatterHeight = height - MARGINS.top - MARGINS.bottom

    // check domains values
    if (
      xDomain.some((d) => isUndefined(d) || isNaN(d)) ||
      yDomain.some((d) => isUndefined(d) || isNaN(d)) ||
      scatterWidth < 0 ||
      scatterHeight < 0
    ) {
      return null
    }

    ///////////////////////////////////////////////////////////////////////////

    const { xScale, yScale, xScaleScrollbar, yScaleScrollbar } = computeScales(
      scatterWidth,
      scatterHeight,
      scaleType,
      xDomain,
      yDomain,
      xDomainGreater,
      yDomainGreater
    )

    return (
      <svg
        className={`-scatterplot ${className} overflow-visible`}
        x={x}
        y={y}
        width={width}
        height={height}
      >
        <defs>
          <clipPath id={CUTOFF_ID}>
            <rect x={MARGINS.left} y={MARGINS.top} width={scatterWidth} height={scatterHeight} />
          </clipPath>
          <marker
            id={ARROW_MARKER_ID}
            viewBox="0 0 10 10"
            refX="5"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" />
          </marker>
          <ShadowFilter
            id={LABEL_SHADOW_ID}
            dx={0.3}
            dy={1}
            stdDeviation={0.6}
            floodColor="rgba(0, 0, 0, 0.1)"
          />
          <filter id={CIRCLE_SHADOW_ID}>
            <feDropShadow dx="0" dy="0" stdDeviation="1" floodColor="black" />
          </filter>
        </defs>

        {debug && (
          <g className="-debug">
            <rect
              className=""
              x={0}
              y={0}
              width={width}
              height={height}
              fill="none"
              stroke="lime"
              strokeWidth={1}
            />
            <rect
              className=""
              x={MARGINS.left}
              y={MARGINS.top}
              width={scatterWidth}
              height={scatterHeight}
              fill="none"
              stroke="purple"
              strokeWidth={1}
            />
          </g>
        )}

        <Grid
          className=""
          xScale={xScale}
          yScale={yScale}
          xScaleScrollbar={xScaleScrollbar}
          yScaleScrollbar={yScaleScrollbar}
        />
        <Paths className="" xScale={xScale} yScale={yScale} clipPath={`url(#${CUTOFF_ID})`} />
        <Dots className="" xScale={xScale} yScale={yScale} />
        <Labels className="" xScale={xScale} yScale={yScale} />

        <g className="-axes">
          <FilterableAxis
            className="-x-axis"
            xScale={xScale}
            yScale={yScale}
            xScaleScrollbar={xScaleScrollbar}
            yScaleScrollbar={yScaleScrollbar}
            type="x"
            onDragChange={setIsXAxisDragging}
            handleRadius={HANDLE_AXIS_RADIUS}
            draggingHandleRadius={DRAGGING_HANDLE_AXIS_RADIUS}
            axisName={xAxisName}
          />
          <FilterableAxis
            className="-y-axis"
            xScale={xScale}
            yScale={yScale}
            xScaleScrollbar={xScaleScrollbar}
            yScaleScrollbar={yScaleScrollbar}
            type="y"
            onDragChange={setIsYAxisDragging}
            handleRadius={HANDLE_AXIS_RADIUS}
            draggingHandleRadius={DRAGGING_HANDLE_AXIS_RADIUS}
            axisName={yAxisName}
          />
        </g>
      </svg>
    )
  })
)
